import * as React from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import { ReactComponent as Logo } from '../assets/img/logo.svg';
import Typography from '@mui/material/Typography';
import useAuth from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import { Paper, Box, styled } from '@mui/material';
import { ErrorAlert } from '../components/common/ErrorAlert';

const StyledPageContainer = styled(Box)({
  minHeight: '100vh',
  width: '100vw',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

const StyledLoginCard = styled(Paper)({
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  borderRadius: '24px',
  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15), 0 10px 20px rgba(102, 126, 234, 0.1)',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '440px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
});

const StyledHeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '32px',
  gap: '16px',
});

const StyledLogoContainer = styled(Box)({
  padding: '12px',
  borderRadius: '16px',
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
  '& svg': {
    filter: 'brightness(0) invert(1)',
  },
});

const StyledTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '32px',
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
});

const StyledTextField = styled(TextField)({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    '&:hover fieldset': {
      borderColor: '#667eea',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#667eea',
  },
});

const StyledButton = styled(Button)({
  borderRadius: '12px',
  padding: '14px 28px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(145deg, #667eea, #764ba2)',
  boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
  marginTop: '8px',
  marginBottom: '24px',
  '&:hover': {
    background: 'linear-gradient(145deg, #5a6fd8, #6a42a0)',
    boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
});

const StyledDivider = styled(Divider)({
  margin: '24px 0 16px 0',
  '&::before, &::after': {
    borderColor: 'rgba(102, 126, 234, 0.2)',
  },
});

const StyledForgotButton = styled(Button)({
  textTransform: 'none',
  color: '#667eea',
  fontWeight: 500,
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': {
    background: 'rgba(102, 126, 234, 0.1)',
  },
});

const StyledCenterBox = styled(Box)({
  textAlign: 'center',
});

export default function SignIn(): JSX.Element {
  const { login, error, loading } = useAuth();
  const navigate = useNavigate();

  // Local state for fields and errors
  const [email, setEmail] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);

  const isValidEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePassword = (password: string): string | null => {
    if (!password) {
      return 'Password is required.';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter.';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter.';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number.';
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    
    let hasError = false;

    if (!email.trim()) {
      setEmailError('Email is required.');
      hasError = true;
    } else if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      hasError = true;
    } else {
      setEmailError(null);
    }

    const passwordValidation = validatePassword(password);
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      hasError = true;
    } else {
      setPasswordError(null);
    }

    if (hasError) return;

    login(email, password);
  };

  const handleForgotPassword = (): void => {
    navigate('/forgot-password');
  };

  return (
    <>
      <CssBaseline />
      <StyledPageContainer>
        <StyledLoginCard elevation={0}>
          <StyledHeaderContainer>
            <StyledLogoContainer>
              <Logo />
            </StyledLogoContainer>
            <StyledTitle>
              Welcome Back
            </StyledTitle>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              sx={{ fontWeight: 400, opacity: 0.8 }}
            >
              Sign in to access your bird monitoring dashboard
            </Typography>
          </StyledHeaderContainer>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {error && (
              <ErrorAlert
                severity="error"
                title="Login Failed"
                message={error.message || error.toString()}
              />
            )}
            <StyledTextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              variant="outlined"
              disabled={loading}
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                // Don't clear email error when typing to preserve validation feedback
              }}
              error={!!emailError}
              helperText={emailError}
            />
            <StyledTextField
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              variant="outlined"
              disabled={loading}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                // Don't clear password error when typing to preserve validation feedback
              }}
              error={!!passwordError}
              helperText={passwordError}
            />
            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </StyledButton>

            <StyledDivider>
              <Typography variant="body2" color="text.secondary">
                Need help?
              </Typography>
            </StyledDivider>

            <StyledCenterBox>
              <StyledForgotButton onClick={handleForgotPassword} disabled={loading}>
                Forgot your password?
              </StyledForgotButton>
            </StyledCenterBox>
          </Box>
        </StyledLoginCard>
      </StyledPageContainer>
    </>
  );
}
